const ACRONYMS = new Set(['api', 'html', 'http', 'id', 'ip', 'json', 'sms', 'uri', 'url', 'uuid'])

// HTTP plumbing ACI groups a tool's inputs by: kept out of labels unless it tells two apart.
const REQUEST_GROUP_SUFFIX = new Map([
  ['path', ''],
  ['query', ' (option)'],
  ['header', ' (header)'],
  ['body', ' (data)'],
])

const WORD = /\p{Lu}+(?!\p{Ll})\d*|\p{Lu}?\p{Ll}+\d*|\p{L}+|\d+/gu

const humanizeWord = (word: string, index: number) => {
  const lower = word.toLowerCase()
  if (ACRONYMS.has(lower)) return lower.toUpperCase()
  if (lower.endsWith('s') && ACRONYMS.has(lower.slice(0, -1))) {
    return `${lower.slice(0, -1).toUpperCase()}s`
  }
  if (/^\p{Lu}{2,}\d*$/u.test(word)) return word
  return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
}

/** `spreadsheetId` → "Spreadsheet ID", `value_input_option` → "Value input option". */
export const humanizeKey = (key: string) => {
  // An all-caps key is a constant name, not a run of acronyms.
  const source = /\p{Ll}/u.test(key) ? key : key.toLowerCase()
  return source.match(WORD)?.map(humanizeWord).join(' ') || key
}

export interface LabelSource {
  path: string[]
  title?: string
  /** Title of each container on the path, outermost first. */
  containerTitles: (string | undefined)[]
}

const requestGroup = ({ path }: LabelSource) =>
  path.length > 1 && REQUEST_GROUP_SUFFIX.has(path[0]) ? path[0] : undefined

const ancestorLabels = (leaf: LabelSource) =>
  leaf.path
    .slice(0, -1)
    .flatMap((segment, depth) =>
      depth === 0 && requestGroup(leaf)
        ? []
        : [leaf.containerTitles[depth] || humanizeKey(segment)],
    )

const duplicated = (labels: string[]) => {
  const counts = new Map<string, number>()
  for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1)
  return (label: string) => (counts.get(label) ?? 0) > 1
}

const suffixClashes = (labels: string[], suffixOf: (index: number) => string) => {
  const clashes = duplicated(labels)
  return labels.map((label, index) => (clashes(label) ? label + suffixOf(index) : label))
}

/** A label per leaf, unique within the tool, disambiguated only where two would read the same. */
export const labelLeaves = (leaves: LabelSource[]): string[] => {
  const base = leaves.map((leaf) => leaf.title || humanizeKey(leaf.path.at(-1) ?? ''))
  const baseClashes = duplicated(base)
  const prefixed = base.map((label, index) =>
    baseClashes(label) ? [...ancestorLabels(leaves[index]), label].join(' › ') : label,
  )
  const groupsByLabel = new Map<string, Set<string | undefined>>()
  prefixed.forEach((label, index) => {
    const groups = groupsByLabel.get(label) ?? new Set()
    groupsByLabel.set(label, groups.add(requestGroup(leaves[index])))
  })
  const grouped = prefixed.map((label, index) => {
    const group = requestGroup(leaves[index])
    const clashesAcrossGroups = (groupsByLabel.get(label)?.size ?? 0) > 1
    return clashesAcrossGroups && group ? label + REQUEST_GROUP_SUFFIX.get(group) : label
  })
  // Names that humanise alike, e.g. Zendesk's `role` and `role[]`.
  const named = suffixClashes(grouped, (index) => ` (${leaves[index].path.at(-1)})`)
  return suffixClashes(named, (index) => ` (${leaves[index].path.join('.')})`)
}
